import type { Dayjs, ConfigType } from 'dayjs';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import customParseFormat from 'dayjs/plugin/customParseFormat';

// 1. Initialize required plugins
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

// Export the original dayjs instance with plugins, but without the timezone wrapper.
// This is useful for operations that need to run in the user's system time.
export const dayjsSystem = dayjs;

// 3. Set the default timezone for tz()-based parsing / now()
const TIMEZONE = 'America/New_York';
dayjs.tz.setDefault(TIMEZONE);

// 4. Create a wrapper that:
//    - uses EST only for "now" and full timestamps
//    - keeps plain YYYY-MM-DD as-is (no timezone shift)
const dayjsEST = ((date?: ConfigType, format?: string, strict?: boolean): Dayjs => {
  // No args -> now in EST
  if (date === undefined && format === undefined && strict === undefined) {
    return dayjs.tz(undefined, TIMEZONE);
  }
  if (dayjs(date).isValid()) {
    // Strict parsing with custom format: keep original behavior
    if (typeof format === 'string' && typeof strict === 'boolean') {
      return dayjs(date as ConfigType, format, strict);
    }

    // For everything else (timestamps, numbers, Date, etc.), use EST
    return dayjs.tz(date as ConfigType, TIMEZONE);
  }

  // Invalid date input: fallback to original dayjs behavior
  return dayjs(date as ConfigType);
}) as typeof dayjs;

// 5. Copy all static properties (extend, locale, isDayjs, etc.) from original dayjs
Object.assign(dayjsEST, dayjs);

export default dayjsEST;
